import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Grid, Chip, Divider, Tabs, Tab, Fab, Stack, Tooltip, TextField, MenuItem,
  Select, FormControl, InputLabel, Checkbox, FormControlLabel, FormGroup,
  IconButton, CircularProgress, Alert, Table, TableHead, TableBody, TableRow,
  TableCell, Avatar, Paper, Snackbar,
} from '@mui/material';
import {
  Add, SportsScore, Groups, CalendarMonth, Place, Email, Phone, EmojiEvents,
  Edit, ManageAccounts, PersonAdd, Search, Close,
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';
import { BASE_URL, apiCall } from '../api/apiClient';

import GamesTab from './GamesTab';
import TeamsTab from './TeamsTab';
import GameDialog from './GameDialog';
import AddTeamDialog from './AddTeamDialog';
import TournamentBracketTab from './TournamentBracket2/TournamentBracketTab';

// ── Design tokens ──────────────────────────────────────────────────────────────
const BG       = '#ffffff';
const SURFACE  = '#f6f8f7';
const BORDER   = '#e2e8e4';
const ACCENT   = '#16a34a';
const ACCENT_BG= '#f0fdf4';
const TEXT_PRI = '#0f1f14';
const TEXT_SEC = '#4b6358';

const statusColor = {
  open: 'success', closed: 'error', upcoming: 'info',
  completed: 'default', draft: 'warning', active: 'success', cancelled: 'error',
};

const sharedPaper = { borderRadius: '16px', bgcolor: BG, color: TEXT_PRI, boxShadow: '0 24px 64px rgba(0,0,0,0.10)' };

const tabsSx = {
  '& .MuiTab-root': { color: TEXT_SEC, fontWeight: 600, fontSize: 13, textTransform: 'none', minHeight: 44 },
  '& .Mui-selected': { color: `${ACCENT} !important` },
  '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 2, borderRadius: 2 },
};

const outlinedBtn = {
  borderColor: BORDER, color: TEXT_PRI, fontWeight: 600,
  textTransform: 'none', borderRadius: '10px',
  '&:hover': { borderColor: ACCENT, color: ACCENT, bgcolor: ACCENT_BG },
};

const InfoRow = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
    {icon}
    <Typography variant="body2" color={TEXT_SEC} sx={{ minWidth: 80 }}>{label}:</Typography>
    <Typography variant="body2" color={TEXT_PRI}>{value || '—'}</Typography>
  </Box>
);

// ─── Edit Event Dialog ────────────────────────────────────────────────────────
const EditEventDialog = ({ open, onClose, event, onRefresh }) => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (event) {
      setForm({
        type: event.type || '',
        format: event.format || '',
        entryType: event.entryType || 'free',
        entryFee: event.entryFee || 0,
        prizePool: event.prizePool || { firstPlace: '', secondPlace: '', thirdPlace: '', currency: 'GNF' },
        location: event.location?.length ? event.location[0] : { name: '', address: '', city: '', country: '' },
        contactInfo: event.contactInfo || { organisator: '', email: '', phone: '' },
        sponsors: event.sponsors || [],
        eventStartDate: event.eventStartDate ? event.eventStartDate.slice(0, 10) : '',
        eventFinishDate: event.eventFinishDate ? event.eventFinishDate.slice(0, 10) : '',
        status: event.status || 'draft',
      });
    }
  }, [event]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const handleNested = (parent, field, value) =>
    setForm(prev => ({ ...prev, [parent]: { ...prev[parent], [field]: value } }));

  const handleSubmit = async () => {
    setLoading(true); setError('');
    try {
      await apiCall((headers) =>
        axios.put(`${BASE_URL}/events/${event._id}`, { ...form, location: [form.location] }, { headers })
      );
      onRefresh(); onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update event');
    } finally { setLoading(false); }
  };

  const sectionLabel = (text) => (
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ mt: 1, color: TEXT_SEC, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {text}
      </Typography>
    </Grid>
  );
  const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: SURFACE,
    color: TEXT_PRI,
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: ACCENT },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
  },
  '& .MuiInputLabel-root': { color: TEXT_SEC },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
  '& .MuiSelect-icon': { color: TEXT_SEC },
};

const menuProps = {
  PaperProps: {
    sx: {
      bgcolor: SURFACE,
      border: `1px solid ${BORDER}`,
      borderRadius: '10px',
      boxShadow: '0 8px 24px rgba(30,107,69,0.10)',
      '& .MuiMenuItem-root': {
        color: TEXT_PRI,
        '&:hover': { bgcolor: ACCENT_BG },
        '&.Mui-selected': { bgcolor: ACCENT_BG, color: ACCENT },
        '&.Mui-selected:hover': { bgcolor: ACCENT_BG },
      },
    },
  },
};

  return (
   <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: sharedPaper }}>
  <DialogTitle sx={{ bgcolor: BG, px: 3, pt: 2.5, pb: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: '10px', bgcolor: ACCENT_BG, color: ACCENT }}>
          <Edit fontSize="small" />
        </Box>
        <Typography variant="h6" fontWeight={700} color={TEXT_PRI} letterSpacing="-0.3px">
          Modifier l'événement
        </Typography>
      </Box>
      <IconButton onClick={onClose} size="small" sx={{ color: TEXT_SEC }}><Close /></IconButton>
    </Box>
  </DialogTitle>
  <Divider sx={{ borderColor: BORDER }} />

  <DialogContent sx={{ bgcolor: BG, pt: 2.5 }}>
    {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}
    <Grid container spacing={2}>

      {/* Type & Format */}
      <Grid item xs={12} sm={6}>
        <TextField select fullWidth size="small" label="Type"
          value={form.type || ''} onChange={e => handleChange('type', e.target.value)} sx={inputSx}>
          {['tournament', 'single_game', 'training', 'trial'].map(v =>
            <MenuItem key={v} value={v} sx={{ color: TEXT_PRI }}>{v}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
     <TextField
  select fullWidth size="small" label="Format"
  value={form.format || ''} onChange={e => handleChange('format', e.target.value)}
  sx={inputSx} SelectProps={{ MenuProps: menuProps }}>
  {['elimination', 'round_robin', 'league', 'playoff', 'group_stage'].map(v =>
    <MenuItem key={v} value={v}>{v}</MenuItem>)}
</TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField select fullWidth size="small" label="Type d'inscription"
          value={form.entryType || 'free'} onChange={e => handleChange('entryType', e.target.value)} sx={inputSx}>
          <MenuItem value="free" sx={{ color: TEXT_PRI }}>Gratuit</MenuItem>
          <MenuItem value="paid" sx={{ color: TEXT_PRI }}>Payant</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Frais d'inscription" type="number"
          value={form.entryFee || 0} onChange={e => handleChange('entryFee', Number(e.target.value))}
          sx={inputSx} />
      </Grid>

      {/* Dotation */}
      {sectionLabel('Dotation')}
      {[['firstPlace', '1ère place'], ['secondPlace', '2ème place'], ['thirdPlace', '3ème place']].map(([key, lbl]) => (
        <Grid item xs={12} sm={3} key={key}>
          <TextField fullWidth size="small" label={lbl} value={form.prizePool?.[key] || ''}
            onChange={e => handleNested('prizePool', key, e.target.value)} sx={inputSx} />
        </Grid>
      ))}
      <Grid item xs={12} sm={3}>
        <TextField fullWidth size="small" label="Devise" value={form.prizePool?.currency || 'GNF'}
          onChange={e => handleNested('prizePool', 'currency', e.target.value)} sx={inputSx} />
      </Grid>

      {/* Lieu */}
      {sectionLabel('Lieu')}
      {[['name', 'Nom du lieu'], ['address', 'Adresse'], ['city', 'Ville'], ['country', 'Pays']].map(([key, lbl]) => (
        <Grid item xs={12} sm={6} key={key}>
          <TextField fullWidth size="small" label={lbl} value={form.location?.[key] || ''}
            onChange={e => handleNested('location', key, e.target.value)} sx={inputSx} />
        </Grid>
      ))}

      {/* Contact */}
      {sectionLabel('Informations de contact')}
      {[['organisator', 'Organisateur'], ['email', 'E-mail'], ['phone', 'Téléphone']].map(([key, lbl]) => (
        <Grid item xs={12} sm={4} key={key}>
          <TextField fullWidth size="small" label={lbl} value={form.contactInfo?.[key] || ''}
            onChange={e => handleNested('contactInfo', key, e.target.value)} sx={inputSx} />
        </Grid>
      ))}

      {/* Dates */}
      {sectionLabel('Dates')}
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Date de début" type="date"
          InputLabelProps={{ shrink: true }} value={form.eventStartDate || ''}
          onChange={e => handleChange('eventStartDate', e.target.value)} sx={inputSx} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Date de fin" type="date"
          InputLabelProps={{ shrink: true }} value={form.eventFinishDate || ''}
          onChange={e => handleChange('eventFinishDate', e.target.value)} sx={inputSx} />
      </Grid>
      <Grid item xs={12} sm={6}>
       <TextField
  select fullWidth size="small" label="Statut"
  value={form.status || 'draft'} onChange={e => handleChange('status', e.target.value)}
  sx={inputSx} SelectProps={{ MenuProps: menuProps }}>
  {['draft', 'open', 'review', 'completed', 'active', 'close', 'cancelled'].map(v =>
    <MenuItem key={v} value={v}>{v}</MenuItem>)}
</TextField>
      </Grid>

    </Grid>
  </DialogContent>

  <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, bgcolor: BG, borderTop: `1px solid ${BORDER}`, gap: 1 }}>
    <Button onClick={onClose} variant="outlined" sx={outlinedBtn}>Annuler</Button>
    <Button onClick={handleSubmit} variant="contained" disabled={loading}
      startIcon={loading ? <CircularProgress size={16} /> : <Edit />}
      sx={{
        bgcolor: ACCENT, borderRadius: '10px', textTransform: 'none', fontWeight: 600,
        boxShadow: '0 4px 14px rgba(30,107,69,0.25)',
        '&:hover': { bgcolor: '#175c39', boxShadow: '0 6px 18px rgba(30,107,69,0.35)' },
        '&.Mui-disabled': { bgcolor: BORDER, color: TEXT_SEC, boxShadow: 'none' },
      }}>
      Enregistrer les modifications
    </Button>
  </DialogActions>
</Dialog>
  );
};

// ─── Delete Confirmation Dialog ───────────────────────────────────────────────
const DeleteOwnerDialog = ({ open, onClose, owner, onConfirm, deleting }) => {
  if (!owner) return null;
  const user = owner.userId;
  const name = typeof user === 'object'
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
    : user || 'this owner';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { ...sharedPaper, overflow: 'visible' } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 4, pb: 1, px: 3, textAlign: 'center' }}>
        <Box sx={{
          width: 64, height: 64, borderRadius: '50%',
          bgcolor: '#fef2f2', display: 'flex', alignItems: 'center',
          justifyContent: 'center', mb: 2, border: '4px solid #fecaca',
        }}>
          <ManageAccounts sx={{ fontSize: 30, color: '#dc2626' }} />
        </Box>
        <Typography variant="h6" fontWeight={700} color={TEXT_PRI} gutterBottom>Retirer le propriétaire ?</Typography>
        <Typography variant="body2" color={TEXT_SEC} sx={{ mb: 0.5 }}>Vous êtes sur le point de retirer</Typography>
        <Typography variant="body1" fontWeight={700} color="#dc2626" sx={{ mb: 0.5 }}>{name}</Typography>
        <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" sx={{ mb: 2 }}>
          {(owner.roles || []).map(r => (
            <Chip key={r} label={r} size="small"
              color={r === 'eventSuperAdmin' ? 'primary' : 'default'} variant="outlined"
              sx={{ borderRadius: '6px', fontSize: 11 }} />
          ))}
        </Stack>
        <Typography variant="caption" color={TEXT_SEC}>
          Cette action révoquera tous leurs rôles pour cet événement.
        </Typography>
      </Box>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: `1px solid ${BORDER}` }}>
        <Button fullWidth onClick={onClose} variant="outlined" disabled={deleting} sx={outlinedBtn}>Annuler</Button>
        <Button fullWidth onClick={onConfirm} variant="contained" color="error" disabled={deleting}
          startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <Close />}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
          {deleting ? 'Suppression…' : 'Retirer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Owners Management Dialog ─────────────────────────────────────────────────
const OwnersDialog = ({ open, onClose, event, onRefresh }) => {
  const [owners, setOwners]           = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [addMode, setAddMode]         = useState(false);
  const [phoneSearch, setPhoneSearch] = useState('');
  const [foundUser, setFoundUser]     = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [gameId, setGameId]           = useState('');
  const [saving, setSaving]           = useState(false);
  const [saveError, setSaveError]     = useState('');
  const [snack, setSnack]             = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]       = useState(false);

  const games = Array.isArray(event?.games) ? event.games : [];

  const fetchOwners = async () => {
    if (!event?._id) return;
    setLoadingOwners(true);
    try {
      const res = await apiCall((headers) => axios.get(`${BASE_URL}/events/${event._id}/owners`, { headers }));
      const data = res.data;
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data
        : Array.isArray(event.eventOwnersAndRoles) ? event.eventOwnersAndRoles : [];
      setOwners(list);
    } catch {
      setOwners(Array.isArray(event.eventOwnersAndRoles) ? event.eventOwnersAndRoles : []);
    } finally { setLoadingOwners(false); }
  };

  useEffect(() => { if (open) fetchOwners(); }, [open]);

  const handleSearchUser = async () => {
    if (!phoneSearch.trim()) return;
    setSearchLoading(true); setSearchError(''); setFoundUser(null);
    try {
      const res = await apiCall((headers) => axios.get(`${BASE_URL}/users?phone=${phoneSearch.trim()}`, { headers }));
      const users = Array.isArray(res.data) ? res.data : res.data?.data || [];
      const match = users.find(u => u.phone === phoneSearch.trim() || u.phone?.includes(phoneSearch.trim()));
      if (match) setFoundUser(match); else setSearchError('No user found with that phone number.');
    } catch { setSearchError('Error searching users.'); }
    finally { setSearchLoading(false); }
  };

  const handleRoleToggle = (role) =>
    setSelectedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);

  const handleAddOwner = async () => {
    if (!foundUser || selectedRoles.length === 0) return;
    setSaving(true); setSaveError('');
    try {
      await apiCall((headers) =>
        axios.post(`${BASE_URL}/events/${event._id}/owners`,
          { userId: foundUser._id, roles: selectedRoles, ...(gameId ? { gameId } : {}) }, { headers })
      );
      setSnack('Owner added successfully!');
      setAddMode(false); setFoundUser(null); setPhoneSearch(''); setSelectedRoles([]); setGameId('');
      fetchOwners(); onRefresh();
    } catch (e) { setSaveError(e?.response?.data?.message || 'Failed to add owner'); }
    finally { setSaving(false); }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiCall((headers) =>
        axios.delete(`${BASE_URL}/events/${event._id}/owners/${deleteTarget._id}`, { headers })
      );
      setSnack('Owner removed successfully!'); setDeleteTarget(null); fetchOwners(); onRefresh();
    } catch (e) { setSnack(e?.response?.data?.message || 'Failed to remove owner'); setDeleteTarget(null); }
    finally { setDeleting(false); }
  };

  const resetAdd = () => {
    setAddMode(false); setFoundUser(null); setPhoneSearch('');
    setSelectedRoles([]); setGameId(''); setSearchError(''); setSaveError('');
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: sharedPaper }}>
        <DialogTitle sx={{ bgcolor: BG, px: 3, pt: 2.5, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: '10px', bgcolor: ACCENT_BG, color: ACCENT }}>
                <ManageAccounts fontSize="small" />
              </Box>
              <Typography variant="h6" fontWeight={700} color={TEXT_PRI} letterSpacing="-0.3px">
                Propriétaires & Rôles
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!addMode && (
                <Button startIcon={<PersonAdd />} variant="contained" size="small" onClick={() => setAddMode(true)}
                  sx={{ bgcolor: ACCENT, borderRadius: '10px', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#15803d' } }}>
                  Ajouter un propriétaire
                </Button>
              )}
              <IconButton onClick={onClose} size="small" sx={{ color: TEXT_SEC }}><Close /></IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <Divider sx={{ borderColor: BORDER }} />

        <DialogContent sx={{ bgcolor: BG, pt: 2.5 }}>
          {addMode && (
            <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: '12px', bgcolor: SURFACE, border: `1px solid ${BORDER}` }}>
              <Typography variant="subtitle1" fontWeight={700} color={TEXT_PRI} sx={{ mb: 2 }}>
                Ajouter un nouveau propriétaire
              </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
  <TextField
    fullWidth
    size="small"
    label="Rechercher par numéro de téléphone"
    value={phoneSearch}
    onChange={e => setPhoneSearch(e.target.value)}
    onKeyDown={e => e.key === 'Enter' && handleSearchUser()}
    InputProps={{
      endAdornment: searchLoading ? <CircularProgress size={16} sx={{ color: ACCENT }} /> : null,
    }}
    sx={{
      '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        bgcolor: SURFACE,
        color: TEXT_PRI,
        '& fieldset': { borderColor: BORDER },
        '&:hover fieldset': { borderColor: ACCENT },
        '&.Mui-focused fieldset': { borderColor: ACCENT },
      },
      '& .MuiInputLabel-root': { color: TEXT_SEC },
      '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
    }}
  />
  <Button
    variant="contained"
    onClick={handleSearchUser}
    disabled={searchLoading}
    startIcon={<Search />}
    sx={{
      bgcolor: ACCENT,
      borderRadius: '10px',
      textTransform: 'none',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      boxShadow: '0 4px 14px rgba(30,107,69,0.25)',
      '&:hover': { bgcolor: '#175c39', boxShadow: '0 6px 18px rgba(30,107,69,0.35)' },
      '&.Mui-disabled': { bgcolor: BORDER, color: TEXT_SEC, boxShadow: 'none' },
    }}
  >
    Rechercher
  </Button>
</Box>

              {searchError && <Alert severity="warning" sx={{ mb: 1.5, borderRadius: '10px' }}>{searchError}</Alert>}

              {foundUser && (
                <Box sx={{ mb: 2, p: 2, bgcolor: BG, borderRadius: '12px', border: `1px solid ${BORDER}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: ACCENT, width: 40, height: 40, fontWeight: 700 }}>
                      {foundUser.firstName?.[0]}{foundUser.lastName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography fontWeight={700} color={TEXT_PRI}>{foundUser.firstName} {foundUser.lastName}</Typography>
                      <Typography variant="caption" color={TEXT_SEC}>
                        {foundUser.phone} · {foundUser.email || 'Aucun e-mail'} · {foundUser.role}
                      </Typography>
                      {foundUser.entity && (
                        <Typography variant="caption" display="block" color={ACCENT}>
                          {typeof foundUser.entity === 'object'
                            ? `${foundUser.entity.name} (${foundUser.entity.type})` : foundUser.entity}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Typography variant="body2" fontWeight={600} color={TEXT_PRI} sx={{ mb: 0.5 }}>Attribuer des rôles :</Typography>
                 <FormGroup row sx={{ gap: 0.5 }}>
  {['eventSuperAdmin', 'eventGameUpdater'].map(role => (
    <FormControlLabel
      key={role}
      control={
        <Checkbox
          checked={selectedRoles.includes(role)}
          onChange={() => handleRoleToggle(role)}
          size="small"
          sx={{
            color: BORDER,
            '&.Mui-checked': { color: ACCENT },
          }}
        />
      }
      label={
        <Typography variant="body2" sx={{ color: TEXT_PRI, fontSize: '0.82rem' }}>
          {role}
        </Typography>
      }
      sx={{
        m: 0,
        px: 1.5,
        py: 0.75,
        borderRadius: '10px',
        border: `1px solid ${selectedRoles.includes(role) ? ACCENT : BORDER}`,
        bgcolor: selectedRoles.includes(role) ? ACCENT_BG : SURFACE,
        transition: 'all 0.15s ease',
      }}
    />
  ))}
</FormGroup>

<FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
  <InputLabel sx={{ color: TEXT_SEC, '&.Mui-focused': { color: ACCENT } }}>
    Limiter à un match (optionnel)
  </InputLabel>
  <Select
    value={gameId}
    label="Limiter à un match (optionnel)"
    onChange={e => setGameId(e.target.value)}
    sx={{
      borderRadius: '10px',
      bgcolor: SURFACE,
      color: TEXT_PRI,
      '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
      '& .MuiSvgIcon-root': { color: TEXT_SEC },
    }}
    MenuProps={{
      PaperProps: {
        sx: {
          bgcolor: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(30,107,69,0.10)',
          mt: 0.5,
        },
      },
    }}
  >
    <MenuItem value="">
      <Typography variant="body2" sx={{ color: TEXT_SEC, fontStyle: 'italic' }}>
        Tous les matchs (aucune restriction)
      </Typography>
    </MenuItem>
    {games.map(game => (
      <MenuItem
        key={game._id}
        value={game._id}
        sx={{ '&:hover': { bgcolor: ACCENT_BG }, '&.Mui-selected': { bgcolor: ACCENT_BG } }}
      >
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ color: TEXT_PRI }}>
            {game.entity1?.name || 'Équipe 1'} vs {game.entity2?.name || 'Équipe 2'}
          </Typography>
          <Typography variant="caption" sx={{ color: TEXT_SEC }}>
            {game.gameDate ? format(new Date(game.gameDate), 'dd MMM yyyy') : ''}
            {game.venue?.name ? ` · ${game.venue.name}` : ''}
            {game.status ? ` · ${game.status}` : ''}
          </Typography>
        </Box>
      </MenuItem>
    ))}
  </Select>
</FormControl>

{gameId && (
  <Alert
    severity="info"
    icon={<SportsScore fontSize="small" />}
    sx={{
      mt: 1,
      borderRadius: '10px',
      bgcolor: ACCENT_BG,
      color: ACCENT,
      border: `1px solid ${BORDER}`,
      '& .MuiAlert-icon': { color: ACCENT },
    }}
  >
    Ce propriétaire n'aura accès qu'au match sélectionné.
  </Alert>
)}
                </Box>
              )}

              {saveError && <Alert severity="error" sx={{ mb: 1, borderRadius: '10px' }}>{saveError}</Alert>}

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button onClick={resetAdd} variant="outlined" size="small" sx={outlinedBtn}>Annuler</Button>
                <Button onClick={handleAddOwner} variant="contained" size="small"
                  disabled={!foundUser || selectedRoles.length === 0 || saving}
                  startIcon={saving ? <CircularProgress size={14} /> : <Add />}
                  sx={{ bgcolor: ACCENT, borderRadius: '10px', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#15803d' } }}>
                  Ajouter le propriétaire
                </Button>
              </Box>
            </Paper>
          )}

          {loadingOwners ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: ACCENT }} />
            </Box>
          ) : owners.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, bgcolor: SURFACE, borderRadius: '12px' }}>
              <ManageAccounts sx={{ fontSize: 48, color: BORDER, mb: 1 }} />
              <Typography color={TEXT_SEC}>Aucun propriétaire assigné pour l'instant</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: SURFACE }}>
                  {['Utilisateur', 'Rôles', 'Match assigné', 'Actions'].map((h, i) => (
                    <TableCell key={h} align={i === 3 ? 'right' : 'left'}
                      sx={{ fontWeight: 700, color: TEXT_SEC, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', border: 'none', py: 1.5 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {owners.map((owner, i) => {
                  const user = owner.userId;
                  const name = typeof user === 'object' ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : user;
                  const phone = typeof user === 'object' ? user.phone : null;
                  const game = owner.gameId;
                  const gameLabel = game ? (typeof game === 'object'
                    ? `${game.entity1?.name || ''} vs ${game.entity2?.name || ''}` : game) : null;

                  return (
                    <TableRow key={owner._id || i} hover
                      sx={{ '&:hover': { bgcolor: ACCENT_BG }, '& td': { borderColor: BORDER } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: ACCENT_BG, color: ACCENT, fontSize: 13, fontWeight: 700 }}>
                            {name?.[0] || '?'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600} color={TEXT_PRI}>{name || '—'}</Typography>
                            {phone && <Typography variant="caption" color={TEXT_SEC}>{phone}</Typography>}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {(owner.roles || []).map(r => (
                            <Chip key={r} label={r} size="small"
                              sx={{
                                borderRadius: '6px', fontSize: 11, fontWeight: 600,
                                bgcolor: r === 'eventSuperAdmin' ? ACCENT_BG : SURFACE,
                                color: r === 'eventSuperAdmin' ? ACCENT : TEXT_SEC,
                                border: `1px solid ${r === 'eventSuperAdmin' ? '#bbf7d0' : BORDER}`,
                              }} />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color={TEXT_SEC}>
                          {gameLabel || '— tous les matchs'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Supprimer le propriétaire">
                          <IconButton size="small" onClick={() => setDeleteTarget(owner)}
                            sx={{ color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' }, borderRadius: '8px', transition: 'background 0.2s' }}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, bgcolor: BG, borderTop: `1px solid ${BORDER}` }}>
          <Button onClick={onClose} variant="outlined" sx={outlinedBtn}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <DeleteOwnerDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        owner={deleteTarget} onConfirm={handleDeleteConfirm} deleting={deleting} />

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const EventDetailDialog = ({ open, onClose, event, onRefresh }) => {
  const [tab, setTab]                     = useState(0);
  const [gameDialogOpen, setGameDialogOpen] = useState(false);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [ownersDialogOpen, setOwnersDialogOpen] = useState(false);

  if (!event) return null;

  const currentUserId = (() => {
    try { const user = localStorage.getItem('user'); if (user) return JSON.parse(user)._id; } catch {}
    return null;
  })();

  const currentUserEntry = event.eventOwnersAndRoles?.find(entry => {
    const entryUserId = typeof entry.userId === 'object' ? entry.userId._id : entry.userId;
    return entryUserId === currentUserId;
  });

  const isOwner = !!currentUserEntry;
  const isSuperAdmin = currentUserEntry?.roles?.includes('eventSuperAdmin');

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
        PaperProps={{ sx: { ...sharedPaper, minHeight: '75vh' } }}>

        {/* ── Header ── */}
        <DialogTitle sx={{ bgcolor: BG, px: 3, pt: 2.5, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: '12px', bgcolor: ACCENT_BG, color: ACCENT }}>
                <EmojiEvents />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} color={TEXT_PRI} letterSpacing="-0.3px">{event.name}</Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.4 }}>
                  <Chip label={event.type} size="small" sx={{ borderRadius: '6px', fontSize: 11, fontWeight: 600, bgcolor: SURFACE, color: TEXT_PRI }} />
                  <Chip label={event.format} size="small" variant="outlined" sx={{ borderRadius: '6px', fontSize: 11, fontWeight: 600, borderColor: BORDER, color: TEXT_SEC }} />
                  <Chip label={event.status} color={statusColor[event.status] || 'default'} size="small" sx={{ borderRadius: '6px', fontSize: 11, fontWeight: 600 }} />
                </Stack>
              </Box>
            </Box>

            {isSuperAdmin && (
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" size="small" startIcon={<ManageAccounts />}
                  onClick={() => setOwnersDialogOpen(true)} sx={outlinedBtn}>
                  Propriétaires
                </Button>
                <Button variant="contained" size="small" startIcon={<Edit />}
                  onClick={() => setEditDialogOpen(true)}
                  sx={{ bgcolor: ACCENT, borderRadius: '10px', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#15803d' } }}>
                  Modifier
                </Button>
              </Stack>
            )}
          </Box>
        </DialogTitle>

        <Divider sx={{ borderColor: BORDER }} />

        <DialogContent sx={{ p: 0, bgcolor: BG }}>
          {/* ── Info banner ── */}
          <Box sx={{ px: 3, py: 2.5, bgcolor: SURFACE, borderBottom: `1px solid ${BORDER}` }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InfoRow icon={<CalendarMonth fontSize="small" sx={{ color: TEXT_SEC }} />} label="Début"
                  value={event.eventStartDate ? format(new Date(event.eventStartDate), 'dd MMM yyyy') : null} />
                <InfoRow icon={<CalendarMonth fontSize="small" sx={{ color: TEXT_SEC }} />} label="Fin"
                  value={event.eventFinishDate ? format(new Date(event.eventFinishDate), 'dd MMM yyyy') : null} />
                <InfoRow icon={<CalendarMonth fontSize="small" sx={{ color: TEXT_SEC }} />} label="Date limite"
                  value={event.applicationDeadline ? format(new Date(event.applicationDeadline), 'dd MMM yyyy HH:mm') : null} />
              </Grid>
              <Grid item xs={12} sm={6}>
                {event.location?.map((loc, i) => (
                  <InfoRow key={i} icon={<Place fontSize="small" sx={{ color: TEXT_SEC }} />} label="Lieu"
                    value={`${loc.name}, ${loc.city}, ${loc.country}`} />
                ))}
                {event.contactInfo?.email && (
                  <InfoRow icon={<Email fontSize="small" sx={{ color: TEXT_SEC }} />} label="E-mail" value={event.contactInfo.email} />
                )}
                {event.contactInfo?.phone && (
                  <InfoRow icon={<Phone fontSize="small" sx={{ color: TEXT_SEC }} />} label="Téléphone" value={event.contactInfo.phone} />
                )}
              </Grid>
            </Grid>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Chip icon={<SportsScore sx={{ fontSize: '14px !important', color: `${ACCENT} !important` }} />}
                label={`${event.games?.length || 0} Matchs`} size="small"
                sx={{ borderRadius: '6px', fontSize: 12, fontWeight: 600, bgcolor: ACCENT_BG, color: ACCENT, border: `1px solid #bbf7d0` }} />
              <Chip icon={<Groups sx={{ fontSize: '14px !important', color: `${TEXT_SEC} !important` }} />}
                label={`${event.entitiesParticipants?.length || 0} Équipes`} size="small"
                sx={{ borderRadius: '6px', fontSize: 12, fontWeight: 600, bgcolor: BG, color: TEXT_PRI, border: `1px solid ${BORDER}` }} />
              {event.entryType && (
                <Chip label={`Inscription : ${event.entryType}`} size="small"
                  sx={{ borderRadius: '6px', fontSize: 12, fontWeight: 600, bgcolor: BG, color: TEXT_SEC, border: `1px solid ${BORDER}` }} />
              )}
            </Stack>
          </Box>

          {/* ── Tabs ── */}
          <Box sx={{ borderBottom: `1px solid ${BORDER}`, px: 3, bgcolor: BG }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={tabsSx}>
              <Tab icon={<SportsScore sx={{ fontSize: 17 }} />} iconPosition="start" label={`Matchs (${event.games?.length || 0})`} />
              <Tab icon={<Groups sx={{ fontSize: 17 }} />} iconPosition="start" label={`Équipes (${event.entitiesParticipants?.length || 0})`} />
              <Tab icon={<EmojiEvents sx={{ fontSize: 17 }} />} iconPosition="start" label="Bracket" />
            </Tabs>
          </Box>

          <Box sx={{ p: 3, pb: isOwner ? 10 : 3, bgcolor: BG }}>
            {tab === 0 && <GamesTab games={event.games} onRefresh={onRefresh} currentUserId={currentUserId} event={event} />}
            {tab === 1 && <TeamsTab event={event} onRefresh={onRefresh} />}
            {tab === 2 && <TournamentBracketTab event={event} />}
          </Box>
        </DialogContent>

        {isSuperAdmin && (
          <Box sx={{ position: 'absolute', bottom: 72, right: 24, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Tooltip title="Ajouter une équipe" placement="left">
              <Fab size="medium" onClick={() => setTeamDialogOpen(true)}
                sx={{ bgcolor: SURFACE, color: TEXT_PRI, boxShadow: '0 4px 12px rgba(0,0,0,0.10)', '&:hover': { bgcolor: ACCENT_BG, color: ACCENT } }}>
                <Groups />
              </Fab>
            </Tooltip>
            <Tooltip title="Ajouter un match" placement="left">
              <Fab size="medium" onClick={() => setGameDialogOpen(true)}
                sx={{ bgcolor: ACCENT, color: '#fff', boxShadow: '0 4px 16px rgba(22,163,74,0.35)', '&:hover': { bgcolor: '#15803d' } }}>
                <Add />
              </Fab>
            </Tooltip>
          </Box>
        )}

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, bgcolor: BG, borderTop: `1px solid ${BORDER}` }}>
          <Button onClick={onClose} variant="outlined" sx={outlinedBtn}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {isSuperAdmin && (
        <>
          <GameDialog open={gameDialogOpen} onClose={() => setGameDialogOpen(false)} eventId={event._id} onCreated={onRefresh} />
          <AddTeamDialog open={teamDialogOpen} onClose={() => setTeamDialogOpen(false)} eventId={event._id} onCreated={onRefresh} />
          <EditEventDialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} event={event} onRefresh={onRefresh} />
          <OwnersDialog open={ownersDialogOpen} onClose={() => setOwnersDialogOpen(false)} event={event} onRefresh={onRefresh} />
        </>
      )}
    </>
  );
};

export default EventDetailDialog;